import { useState, useEffect, useCallback, useRef } from 'react'
import {
  supabase, patientsApi, encountersApi, mseApi, emApi,
  carePlanApi, medicationsApi, allergiesApi, diagnosesApi,
  vitalsApi, assessmentsApi, messagingApi, appointmentsApi
} from '../lib/supabase'

// ── Generic fetch hook ────────────────────────────────────────────────────────
export function useQuery(fn, deps = [], options = {}) {
  const [data,    setData]    = useState(options.initial ?? null)
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const load = useCallback(async () => {
    if (options.skip) { setLoading(false); return }
    setLoading(true); setError(null)
    try {
      const { data: d, error: e } = await fn()
      if (e) throw e
      setData(d)
    } catch (err) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
    // useQuery-style API: the caller supplies the dependency array (like a
    // query key); fn is intentionally excluded because callers recreate it
    // every render. Static analysis cannot see caller-owned deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  useEffect(() => { load() }, [load])
  return { data, loading, error, reload: load, setData }
}

// ── Patients ──────────────────────────────────────────────────────────────────
// 2026-08-05 fix: had a hardcoded fallback default ('00000000-...-0001', a
// dev/placeholder UUID) that was silently used by the one real caller in
// this codebase (PatientsPage.jsx called usePatients() with no argument at
// all) — the main patient list was showing the fake org's patients for
// every real visit. Removed the default entirely: a caller must now pass a
// real orgId, and the hook simply doesn't fetch until one is provided,
// rather than ever silently falling back to a fake org again.
export function usePatients(orgId) {
  const [patients, setPatients] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)

  const load = useCallback(async () => {
    if (!orgId) {
      setPatients([])
      setError('No active organization context is available.')
      setLoading(false)
      return
    }
    setLoading(true)
    const { data, error } = await patientsApi.list(orgId)
    if (error) setError(error.message)
    else setPatients(data || [])
    setLoading(false)
  }, [orgId])

  useEffect(() => { load() }, [load])

  const createPatient = async (formData) => {
    if (!orgId) throw new Error('Patient registration requires a verified organization context.')
    // Audit 2026-07-28 (defect #19): the old payload carried seven columns
    // that do not exist on cr.patient (rowguid, purge, partner_patient,
    // work_phone, emergency_contact, emerg_phone, emerg_relation) — the
    // insert 400'd on every save. Canonical mapping below; emergency
    // contact goes to its real table, country code to country_code.
    // work_phone renamed _work_phone: still a load-bearing rest-omit (must stay out of payload)
    const { emergency_contact, emerg_phone, emerg_relation, work_phone: _work_phone, country, ...rest } = formData
    const payload = {
      ...rest, org_id: orgId,
      country_code: country || null,   // ISO-2 → canonical column (not free-text `country`)
      insert_date: new Date().toISOString(),
      update_date: new Date().toISOString(),
    }
    const { data, error } = await patientsApi.create(payload)
    if (error) throw error
    if (emergency_contact && emergency_contact.trim()) {
      const { error: emergErr } = await supabase.from('pat_emergcontact').insert({
        patient_id: data.patient_id, org_id: orgId,
        contact_name: emergency_contact,
        relationship: emerg_relation || null,
        primary_phone: emerg_phone || null,
        insert_date: new Date().toISOString(),
      })
      if (emergErr) console.error('emergency contact save failed:', emergErr.message)
    }
    setPatients(prev => [data, ...prev])
    return data
  }

  return { patients, loading, error, reload: load, createPatient }
}

export function usePatient(id) {
  return useQuery(() => patientsApi.get(id), [id], { skip: !id })
}

// ── Encounter ─────────────────────────────────────────────────────────────────
export function useEncounter(encounterId) {
  return useQuery(() => encountersApi.get(encounterId), [encounterId], { skip: !encounterId })
}

// ── Mental Status Exam ────────────────────────────────────────────────────────
export function useMSE(patientId, encounterId) {
  const q = useQuery(
    () => mseApi.get(patientId, encounterId),
    [patientId, encounterId],
    { skip: !patientId || !encounterId }
  )

  const save = async (mseData) => {
    const { data, error } = await mseApi.save({
      ...mseData, patient_id: patientId, encounter_id: encounterId,
    })
    if (error) throw error
    q.setData(data)
    return data
  }

  return { ...q, save }
}

// ── E&M Note ──────────────────────────────────────────────────────────────────
export function useEMNote(encounterId) {
  const q = useQuery(() => emApi.get(encounterId), [encounterId], { skip: !encounterId })

  const save = async (emData) => {
    const { data, error } = await emApi.save({ ...emData, encounter_id: encounterId })
    if (error) throw error
    q.setData(data)
    return data
  }

  return { ...q, save }
}

// ── Care Plan ─────────────────────────────────────────────────────────────────
export function useCarePlan(patientId) {
  const q = useQuery(() => carePlanApi.get(patientId), [patientId], { skip: !patientId })

  const save = async (data) => {
    const { data: saved, error } = await carePlanApi.save({ ...data, patient_id: patientId })
    if (error) throw error
    q.setData(saved)
    return saved
  }

  const addGoal = async (goalData) => {
    const { data, error } = await carePlanApi.addGoal({ ...goalData, patient_id: patientId })
    if (error) throw error
    q.reload()
    return data
  }

  return { ...q, save, addGoal }
}

// ── Medications ───────────────────────────────────────────────────────────────
export function useMedications(patientId) {
  const [meds,    setMeds]    = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const load = useCallback(async () => {
    if (!patientId) { setLoading(false); return }
    setLoading(true)
    const { data, error } = await medicationsApi.current(patientId)
    if (error) setError(error.message)
    else setMeds(data || [])
    setLoading(false)
  }, [patientId])

  useEffect(() => { load() }, [load])

  const add = async (medData) => {
    const { data, error } = await medicationsApi.add({ ...medData, patient_id: patientId })
    if (error) throw error
    setMeds(prev => [data, ...prev])
    return data
  }

  const discontinue = async (medId, reason) => {
    await medicationsApi.discontinue(medId, reason)
    setMeds(prev => prev.filter(m => m.patient_med_id !== medId))
  }

  const searchDrug = async (term) => {
    const { data } = await medicationsApi.searchDrug(term)
    return data || []
  }

  return { meds, loading, error, reload: load, add, discontinue, searchDrug }
}

// ── Allergies ─────────────────────────────────────────────────────────────────
export function useAllergies(patientId) {
  const [allergies, setAllergies] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)

  const load = useCallback(async () => {
    if (!patientId) { setLoading(false); return }
    setLoading(true)
    const { data, error } = await allergiesApi.list(patientId)
    if (error) setError(error.message)
    else setAllergies(data || [])
    setLoading(false)
  }, [patientId])

  useEffect(() => { load() }, [load])

  const add = async (allergyData) => {
    const { data, error } = await allergiesApi.add({ ...allergyData, patient_id: patientId })
    if (error) throw error
    setAllergies(prev => [data, ...prev])
    return data
  }

  const remove = async (allergyId) => {
    await allergiesApi.delete(allergyId)
    setAllergies(prev => prev.filter(a => a.allergy_id !== allergyId))
  }

  return { allergies, loading, error, reload: load, add, remove }
}

// ── Diagnoses ─────────────────────────────────────────────────────────────────
export function useDiagnoses(patientId, encounterId) {
  const [diagnoses, setDiagnoses] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)

  const load = useCallback(async () => {
    if (!patientId || !encounterId) { setLoading(false); return }
    setLoading(true)
    const { data, error } = await diagnosesApi.list(patientId, encounterId)
    if (error) setError(error.message)
    else setDiagnoses(data || [])
    setLoading(false)
  }, [patientId, encounterId])

  useEffect(() => { load() }, [load])

  const add = async (diagData) => {
    const { data, error } = await diagnosesApi.add({
      ...diagData, patient_id: patientId, encounter_id: encounterId,
    })
    if (error) throw error
    setDiagnoses(prev => [data, ...prev])
    return data
  }

  const remove = async (diagId) => {
    await diagnosesApi.remove(diagId)
    setDiagnoses(prev => prev.filter(d => d.diag_id !== diagId))
  }

  const searchIcd10 = async (term) => {
    const { data } = await diagnosesApi.searchIcd10(term)
    return data || []
  }

  const searchDsm5 = async (term) => {
    const { data } = await diagnosesApi.searchDsm5(term)
    return data || []
  }

  return { diagnoses, loading, error, reload: load, add, remove, searchIcd10, searchDsm5 }
}

// ── Vitals ────────────────────────────────────────────────────────────────────
export function useVitals(patientId) {
  const [vitals,  setVitals]  = useState([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState(null)

  const load = useCallback(async () => {
    if (!patientId) { setLoading(false); return }
    const { data, error } = await vitalsApi.list(patientId)
    if (error) setError(error.message)
    else setVitals(data || [])
    setLoading(false)
  }, [patientId])

  useEffect(() => { load() }, [load])

  const add = async (vitalsData) => {
    const { data, error } = await vitalsApi.add({ ...vitalsData, patient_id: patientId })
    if (error) throw error
    setVitals(prev => [data, ...prev])
    return data
  }

  return { vitals, loading, error, reload: load, add }
}

// ── Assessments ───────────────────────────────────────────────────────────────
export function useAssessmentHistory(patientId, scaleCode, limit = 12) {
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!patientId) { setLoading(false); return }
    assessmentsApi.history(patientId, scaleCode, limit).then(({ data }) => {
      setHistory((data || []).reverse())
      setLoading(false)
    })
  }, [patientId, scaleCode, limit])

  const save = async (adminData, responses, scores) => {
    const result = await assessmentsApi.saveAdministration(adminData, responses, scores)
    setHistory(prev => [...prev, {
      administered_at: new Date().toISOString(),
      total_score: scores.total_score,
      severity_label: scores.severity_label ?? scores.severity_level,
    }])
    return result
  }

  return { history, loading, save }
}

export function useAssessmentAlerts() {
  const [alerts, setAlerts] = useState([])
  useEffect(() => {
    assessmentsApi.alerts().then(({ data }) => setAlerts(data || []))
  }, [])
  const acknowledge = async (scoreId) => {
    await assessmentsApi.acknowledge(scoreId)
    setAlerts(prev => prev.filter(a => a.admin_id !== scoreId))
  }
  return { alerts, acknowledge }
}

// ── Messaging ─────────────────────────────────────────────────────────────────
export function useThreads(userId) {
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)

  const [error, setError] = useState(null)
  const load = useCallback(async () => {
    if (!userId) { setThreads([]); setLoading(false); return }
    const { data, error: qErr } = await messagingApi.threads(userId)
    if (qErr) setError(qErr.message || 'Secure messaging is unavailable.')
    setThreads(data || [])
    setLoading(false)
  }, [userId])

  useEffect(() => { load() }, [load])
  return { threads, loading, error, reload: load, setThreads }
}

export function useMessages(threadId) {
  const [messages, setMessages] = useState([])
  const [loading,  setLoading]  = useState(true)
  const channelRef = useRef(null)

  useEffect(() => {
    if (!threadId) { setMessages([]); setLoading(false); return }
    setLoading(true)
    messagingApi.messages(threadId).then(({ data }) => {
      setMessages(data || [])
      setLoading(false)
    })
    channelRef.current = messagingApi.subscribe(threadId, (payload) => {
      setMessages(prev => [...prev, payload.new])
    })
    return () => channelRef.current?.unsubscribe()
  }, [threadId])

  const send = async (senderId, text) => {
    const msg = {
      thread_id: threadId, sender_id: senderId,
      message_text: text, sent_at: new Date().toISOString(),
    }
    const { data, error } = await messagingApi.send(msg)
    if (error) throw error
    setMessages(prev => prev.some(item => item.message_id === data.message_id) ? prev : [...prev, data])
    return data
  }

  return { messages, loading, send }
}

// ── Appointments ──────────────────────────────────────────────────────────────
export function useAppointments(providerId, weekStart) {
  const [appointments, setAppointments] = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState(null)

  const weekStartIso = weekStart ? weekStart.toISOString() : null

  const load = useCallback(async () => {
    if (!providerId || !weekStartIso) return
    setLoading(true)
    const start = new Date(weekStartIso)
    const end = new Date(weekStartIso)
    end.setDate(end.getDate() + 6)
    const { data, error } = await appointmentsApi.range(
      start.toISOString().slice(0, 10),
      end.toISOString().slice(0, 10),
      providerId
    )
    if (error) setError(error.message)
    else setAppointments(data || [])
    setLoading(false)
  }, [providerId, weekStartIso])

  useEffect(() => { load() }, [load])

  const book = async (apptData) => {
    const { data, error } = await appointmentsApi.create(apptData)
    if (error) throw error
    setAppointments(prev => [...prev, data]
      .sort((a, b) => a.appointment_date.localeCompare(b.appointment_date) || a.appointment_time.localeCompare(b.appointment_time)))
    return data
  }

  const cancel = async (apptId, reason) => {
    await appointmentsApi.cancel(apptId, reason)
    setAppointments(prev => prev.filter(a => a.appointment_id !== apptId))
  }

  return { appointments, loading, error, reload: load, book, cancel }
}

export function useProviders() {
  const [providers, setProviders] = useState([])
  useEffect(() => {
    appointmentsApi.providers().then(({ data }) => setProviders(data || []))
  }, [])
  return providers
}
